import {
  createContext,
  useEffect,
  FC,
  useCallback,
  useContext,
  useMemo,
  useState,
  useRef,
} from "react";
import { isEqual, map, reduce } from "lodash-es";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useRouteMatch } from "react-router";

import { IAPIState } from "shared/types";
import { IUserPermissions, mapUserPermissions } from "shared/utils";
import {
  dispatchGetAllInfoBlocksAction,
  dispatchGetStageAction,
  infoBlocksSelector,
  stageMetaSelector,
  stageSelectorCreator,
} from "Venues/state";
import {
  IEquipmentCategoryItem,
  IStage,
  IVenueBaseInfoBlocks,
  IInfoBlockId,
} from "Venues/types";
import { mergeEquipmentCategories } from "Venues/utils";
import { STAGE_PAGE_BASE_MATCH } from "Venues/router";
import { SuggestEditForm, SuggestEditFormType } from "Venues/components";

interface StageContextState {
  stageId: string | null;
  stage: IStage | null;
  userStagePermissions: IUserPermissions;
  fetchingStage: boolean;
  equipmentCategories: IEquipmentCategoryItem[];
  stageHeaderPlaceholder: IStage | null;
  stageInfoBlocks: IVenueBaseInfoBlocks;
  setStageHeaderPlaceholder: (stage: IStage) => void;
  fetchStage: () => void;
  openSuggestStageEditModal: (props: {
    type: SuggestEditFormType;
    stageId?: string;
    infoBlockSlug?: string;
    categorySlug?: string;
  }) => void;
}

const initialState: StageContextState = {
  stageId: null,
  stage: null,
  userStagePermissions: {},
  fetchingStage: false,
  equipmentCategories: [],
  stageHeaderPlaceholder: null,
  stageInfoBlocks: {},
  setStageHeaderPlaceholder: () => {},
  fetchStage: () => {},
  openSuggestStageEditModal: () => {},
};

const StagePageContext = createContext<StageContextState>(initialState);

export const StagePageProvider: FC = ({ children }) => {
  const match = useRouteMatch(STAGE_PAGE_BASE_MATCH);
  const [suggestStageEdit, setSuggestStageEdit] =
    useState<{
      showModal: boolean;
      type: SuggestEditFormType;
      stageId?: string;
      infoBlockSlug?: string;
      categorySlug?: string;
    }>();

  const dispatch = useDispatch();
  const { stageId } =
    useParams<{
      stageId: string;
    }>();
  const { api } = useSelector(stageMetaSelector(stageId));

  const fetchingStage = useMemo(() => api?.status === IAPIState.pending, [api]);

  const stageIdSelector = useMemo(
    () => stageSelectorCreator(stageId || ""),
    [stageId]
  );
  const stage = useSelector(stageIdSelector);

  const [stageHeaderPlaceholder, setStageHeaderPlaceholder] = useState(stage);

  const userStagePermissions = useMemo(
    () => (stage?.permissions ? mapUserPermissions(stage.permissions) : {}),
    [stage?.permissions]
  );

  // By default forces a fetch of the stage, if the stage should only be fetched if it is not already fetched call method with false parameter
  const fetchStage = useCallback(
    (forceFetch: boolean = true) =>
      stageId && dispatchGetStageAction(dispatch, stageId, forceFetch),
    [dispatch, stageId]
  );

  // fetch the stage (only stage get has the info block ids so this is necessary)
  useEffect(() => {
    // make sure the stage page is matched
    if (match) {
      fetchStage(false);
    }
  }, [fetchStage, match]);

  const stageInfoBlocks = useSelector(
    infoBlocksSelector(
      reduce(
        stage?.infoBlocks,
        (arr, { infoBlock }) =>
          infoBlock?.data ? [...arr, infoBlock.data.id] : arr,
        [] as string[]
      )
    )
  );

  // fetch all the stages info blocks
  // do not dispatch if there are no new infoBlocks on the stage
  const infoBlocksRef = useRef<IInfoBlockId[]>();
  useEffect(() => {
    if (stage) {
      if (!isEqual(infoBlocksRef.current, stage?.infoBlocks)) {
        dispatchGetAllInfoBlocksAction(dispatch, stage.infoBlocks, "stage");

        infoBlocksRef.current = stage?.infoBlocks;
      }
    }
  }, [stage, dispatch]);

  const equipmentCategories: IEquipmentCategoryItem[] = useMemo(() => {
    if (stage?.equipmentCategories) {
      const allCategories = mergeEquipmentCategories(stage.equipmentCategories);
      // for users with editing rights, show all categories
      if (userStagePermissions.EDIT) {
        // merge all (hardcoded) categories with info from API
        return allCategories;
      }

      // for visitors without editing rights, show only categories with text/equipment
      const shownCategories = map(stage.equipmentCategories, "category");

      return allCategories.filter((item) =>
        shownCategories.includes(item.category)
      );
    }

    return [];
  }, [stage?.equipmentCategories, userStagePermissions]);

  const openSuggestStageEditModal = (props: { type: SuggestEditFormType }) => {
    setSuggestStageEdit({ showModal: true, ...props });
  };

  return (
    <StagePageContext.Provider
      value={{
        stageId,
        stage,
        userStagePermissions,
        fetchingStage,
        equipmentCategories,
        stageHeaderPlaceholder,
        stageInfoBlocks,
        setStageHeaderPlaceholder,
        fetchStage,
        openSuggestStageEditModal,
      }}
    >
      {suggestStageEdit?.showModal && (
        <SuggestEditForm
          handleClose={() => setSuggestStageEdit(undefined)}
          type={suggestStageEdit.type}
          infoBlockSlug={suggestStageEdit.infoBlockSlug}
          categorySlug={suggestStageEdit.categorySlug}
          stageId={suggestStageEdit?.stageId || stage?.id}
        />
      )}
      {children}
    </StagePageContext.Provider>
  );
};

export const useStagePageContext = () => useContext(StagePageContext);
